import type { SpacingToken } from '../../../themes/types';
import type { StyleResolverContext, CssFragment } from './index';
import type { SpacingValue } from './layout';
import { resolveSpacingValue } from './layout';

// ============================================================================
// Types
// ============================================================================

export interface DimensionProps {
  h?: SpacingValue;
  w?: SpacingValue;
  fullH?: boolean;
  fullW?: boolean;
  minH?: SpacingValue;
  maxH?: SpacingValue;
  minW?: SpacingValue;
  maxW?: SpacingValue;
}

export interface ResolvedDimensions {
  height: string | undefined;
  width: string | undefined;
  minHeight: string | undefined;
  maxHeight: string | undefined;
  minWidth: string | undefined;
  maxWidth: string | undefined;
}

// ============================================================================
// Resolution helpers
// ============================================================================

const resolveDimensions = (props: DimensionProps, getSpacing: (token: SpacingToken) => string): ResolvedDimensions => ({
  height: props.fullH ? '100%' : resolveSpacingValue(props.h, getSpacing),
  width: props.fullW ? '100%' : resolveSpacingValue(props.w, getSpacing),
  minHeight: resolveSpacingValue(props.minH, getSpacing),
  maxHeight: resolveSpacingValue(props.maxH, getSpacing),
  minWidth: resolveSpacingValue(props.minW, getSpacing),
  maxWidth: resolveSpacingValue(props.maxW, getSpacing),
});

/** Returns CSS fragments for dimension properties (height, width, min/max). */
const resolveDimensionsCss = (props: DimensionProps, getSpacing: (token: SpacingToken) => string): CssFragment[] => {
  const d = resolveDimensions(props, getSpacing);
  return [
    d.height ? `height: ${d.height};` : null,
    d.width ? `width: ${d.width};` : null,
    d.minHeight ? `min-height: ${d.minHeight};` : null,
    d.maxHeight ? `max-height: ${d.maxHeight};` : null,
    d.minWidth ? `min-width: ${d.minWidth};` : null,
    d.maxWidth ? `max-width: ${d.maxWidth};` : null,
  ];
};

// ============================================================================
// Picker + style resolver
// ============================================================================

const pickDimensionsProps = <T extends Partial<DimensionProps>>(props: T): DimensionProps => {
  const { h, w, fullH, fullW, minH, maxH, minW, maxW } = props;
  return { h, w, fullH, fullW, minH, maxH, minW, maxW };
};

export const resolveDimensionsStyleCss = (props: Record<string, unknown>, theme: StyleResolverContext): CssFragment[] =>
  resolveDimensionsCss(pickDimensionsProps(props as Partial<DimensionProps>), theme.getSpacing);
